import Tooltip from '@atlaskit/tooltip';
import Select, { type StylesConfig } from '@databases/atlaskit/Select';
import { getCompactSelectStyles } from '@databases/atlaskit/compact-select-styles';
import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { type JiraServer } from '@databases/types/confluence';
import { loadJiraServers } from '@databases/utils/redux/jira/actions';
import React, { type FunctionComponent, useEffect, useCallback } from 'react';
import { useAsync } from 'react-async';
import { useIntl } from 'react-intl-next';

const useGetOptionValue = () => {
  return (value: JiraServer) => value.cloudId ?? '';
};

const getOptionLabel = (value: JiraServer) => value.name;

const formatOptionLabel = (value: JiraServer) => value.name;

function validate(
  value: string | undefined,
  isRequired: boolean,
  validationMessage: string
): string | undefined {
  if (isRequired && !value) {
    return validationMessage;
  }
}

const styles: StylesConfig<JiraServer> = {
  menu: (base) => ({ ...base, width: 'max-content', minWidth: '100%' })
};

const compactStyles: StylesConfig<JiraServer> = {
  ...styles,
  ...getCompactSelectStyles<JiraServer>()
};

interface JiraServerPickerProps {
  value: string | undefined;
  onChange: (value: JiraServer | undefined) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isSeamless?: boolean;
  isRequired?: boolean;
  isClearable?: boolean;
  isCompact?: boolean;
  inputId?: string;
}

const JiraServerPicker: FunctionComponent<JiraServerPickerProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isInvalid,
  isDisabled,
  isSeamless,
  isRequired = false,
  isClearable,
  isCompact = false,
  inputId
}) => {
  const { formatMessage } = useIntl();

  const dispatch = defaultStoreUtils.useDispatch();
  const optionsAsync = useAsync({
    promiseFn: useCallback(async () => {
      return await dispatch(loadJiraServers());
    }, [dispatch])
  });

  const resolvedValue =
    value != null
      ? optionsAsync.data?.find((server) => server.cloudId === value)
      : undefined;

  const handleChange = useCallback(
    (val: JiraServer | null) => {
      if (!val) {
        onChange(undefined);
        return;
      }
      onChange(val);
    },
    [onChange]
  );

  useEffect(() => {
    if (value) {
      const serverIdBasedSite = optionsAsync.data?.find((server) => server.id === value);
      if (serverIdBasedSite) {
        onChange(serverIdBasedSite);
      }
    }
  }, [onChange, optionsAsync.data, value]);

  const isServersUnsupported = optionsAsync.isResolved && !optionsAsync.data;

  const getOptionValue = useGetOptionValue();

  return (
    <ValidationStatus
      error={validate(
        value,
        !isServersUnsupported && isRequired,
        formatMessage(messages['validation.required-jira-instance'])
      )}
    >
      {(validationProps) => (
        <Tooltip
          delay={0}
          content={
            isServersUnsupported
              ? formatMessage(
                  messages['field-settings.type-jira-issue-jira-instance-tooltip']
                )
              : undefined
          }
        >
          <Select<JiraServer>
            inputId={inputId}
            value={resolvedValue}
            options={optionsAsync.data}
            onChange={handleChange}
            isDisabled={isDisabled || isServersUnsupported || optionsAsync.isPending}
            isInvalid={validationProps.isInvalid || isInvalid}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            formatOptionLabel={formatOptionLabel}
            onFocus={onFocus}
            onBlur={onBlur}
            autoFocus={autoFocus}
            styles={isCompact ? compactStyles : styles}
            spacing={isCompact ? 'compact' : undefined}
            isSeamless={isSeamless}
            isLoading={optionsAsync.isPending}
            isClearable={isClearable}
            classNamePrefix="jira-server-picker"
            placeholder={
              isServersUnsupported
                ? formatMessage(
                    messages['field-settings.type-jira-issue-jira-instance-placeholder']
                  )
                : formatMessage(messages['select-dropdown.placeholder'])
            }
          />
        </Tooltip>
      )}
    </ValidationStatus>
  );
};

export default JiraServerPicker;
